'use client';

import { memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  EdgeLabelRenderer,
  Position,
  useReactFlow,
  type EdgeProps,
} from '@xyflow/react';
import { useWorkspaceActions, useWorkspaceItem } from '@/components/workspace/store';
import { ConnectorEdgeToolbar } from '@/components/workspace/connector-edge-toolbar';
import { cn } from '@/lib/utils';
import type { AnnotationStroke, ConnectorArrow } from '@/lib/workspaces/types';

type EdgeShape = 'bezier' | 'smoothstep' | 'straight';
type Point = { x: number; y: number };

// Waypoints are stored in the source→target reference frame so the
// edge keeps its SHAPE when either endpoint node moves around the
// canvas. Concretely each waypoint is `(t, n)` where:
//   t  — parametric position along the source→target axis. 0 ≡
//        source endpoint, 1 ≡ target endpoint, values outside
//        [0, 1] still work (the bend simply sits before/after the
//        endpoints, useful for loops).
//   n  — perpendicular offset from that axis, in the same length
//        units. Positive `n` sits to the LEFT of the source→target
//        vector (90° CCW rotation), negative to the RIGHT.
// When the source or target moves we re-derive the absolute flow
// coords on render, so the entire bend stack pans / rotates with
// the line. See `relToAbs` / `absToRel` below for the math.
//
// Legacy `{ x: number; y: number }` waypoints (saved before this
// change shipped) are still understood by the renderer and lazily
// re-emitted as relative the next time the user drags one. This
// keeps existing workspaces from snapping their bends back to
// straight lines on first load.
type WaypointRel = { t: number; n: number };
type WaypointAbs = { x: number; y: number };
type Waypoint = WaypointRel | WaypointAbs;

function isRelative(wp: Waypoint): wp is WaypointRel {
  return 't' in wp && 'n' in wp;
}

function relToAbs(wp: WaypointRel, s: Point, t: Point): Point {
  const dx = t.x - s.x;
  const dy = t.y - s.y;
  // Perpendicular unit vector, rotated 90° CCW from the axis.
  // We multiply `n` by the perpendicular directly (not normalised)
  // so the bend scales with the source→target distance — moving
  // the target away from the source proportionally widens the
  // curve, which matches user intuition when stretching a line.
  return {
    x: s.x + wp.t * dx + wp.n * -dy,
    y: s.y + wp.t * dy + wp.n * dx,
  };
}

function absToRel(p: Point, s: Point, t: Point): WaypointRel {
  const dx = t.x - s.x;
  const dy = t.y - s.y;
  const mag2 = dx * dx + dy * dy;
  // Source == target ⇒ axis collapses; fall back to a relative
  // (0, 0) so the waypoint sits on top of the source. This is an
  // exceptional case (you can't actually draw a zero-length
  // connector through the UI) but the math needs to stay defined.
  if (mag2 < 1e-3) return { t: 0, n: 0 };
  const rx = p.x - s.x;
  const ry = p.y - s.y;
  return {
    t: (rx * dx + ry * dy) / mag2,
    n: (rx * -dy + ry * dx) / mag2,
  };
}

function toAbsolute(wp: Waypoint, s: Point, t: Point): Point {
  return isRelative(wp) ? relToAbs(wp, s, t) : wp;
}

// The connector handle dots are 10px diameter (5px radius) and sit
// centred on the node edge. React Flow reports `sourceX/targetX` at
// that centre, so the path naturally ends UNDER the dot — and since
// the React Flow handle layer renders above the edges SVG, the dot
// then obscures the arrowhead's tip.
//
// We compensate by pushing each endpoint slightly outward along the
// handle's facing direction. The path now exits the node, passes
// behind the dot, and emerges with the arrowhead visible just past
// the dot's outer edge. This matches Figma / Miro's "arrow touches
// the node" visual without any per-handle visibility bookkeeping.
const HANDLE_RADIUS = 6;

// Minimum distance the path runs PERPENDICULAR to the source / target
// handle before it's allowed to bend. Prevents the connector from
// running flush along a widget edge (the "0° / 180° looks ugly"
// complaint) — same lead-in / lead-out trick React Flow's stock
// shapes example uses to keep edges visually distinct from the
// nodes they connect. Only enforced when the next / previous point
// actually sits in the handle's outward direction (`shouldLead`),
// otherwise forcing the lead-in would double-back through the
// widget and produce an even uglier U-turn.
const KEEP_OUT = 24;

function shiftOutward(
  x: number,
  y: number,
  position: Position,
  delta: number,
): [number, number] {
  switch (position) {
    case Position.Top:
      return [x, y - delta];
    case Position.Bottom:
      return [x, y + delta];
    case Position.Left:
      return [x - delta, y];
    case Position.Right:
    default:
      return [x + delta, y];
  }
}

// Outgoing unit vector for a handle facing a given side. Used to
// build endpoint tangents for the smooth bezier path so the curve
// leaves / enters each node perpendicular to its face, matching
// React Flow's stock bezier look even when waypoints are present.
function tangentOf(pos: Position): Point {
  switch (pos) {
    case Position.Top:
      return { x: 0, y: -1 };
    case Position.Bottom:
      return { x: 0, y: 1 };
    case Position.Left:
      return { x: -1, y: 0 };
    case Position.Right:
    default:
      return { x: 1, y: 0 };
  }
}

function normalise(v: Point): Point {
  const m = Math.hypot(v.x, v.y);
  if (m === 0) return { x: 0, y: 0 };
  return { x: v.x / m, y: v.y / m };
}

/**
 * True when `point` sits at least KEEP_OUT distance in front of
 * `anchor` along the outward direction of `position` — i.e. the
 * handle's perpendicular lead-in / lead-out won't double back
 * through the widget if we insert it. Used to gate keep-out
 * insertion when the user has dragged the connector to a handle
 * that points away from the next path point.
 */
function shouldLead(point: Point, anchor: Point, position: Position): boolean {
  const t = tangentOf(position);
  const dx = point.x - anchor.x;
  const dy = point.y - anchor.y;
  return dx * t.x + dy * t.y >= KEEP_OUT;
}

/**
 * Build a smooth cubic-bezier path through `points`, with the
 * endpoints honouring the source / target handle orientations.
 *
 * Algorithm (per segment Pi → P_{i+1}):
 *   - Tangent at Pi: source handle direction (i == 0), reversed
 *     target handle direction (i == N-1), or chord through
 *     P_{i-1} and P_{i+1} (interior — Catmull-Rom style).
 *   - Control points sit a third of the segment length along each
 *     tangent, which gives a soft curve without overshoot.
 *
 * Why we don't reuse `getBezierPath`: that helper only handles a
 * single source→target segment. Stitching multiple calls together
 * would produce visible kinks at every waypoint because the
 * tangents at the seam don't line up. Computing tangents up-front
 * and emitting one continuous path keeps the curve C1-smooth.
 */
/**
 * Build the bezier path AND surface the per-segment control points
 * so callers can compute the geometric midpoint that actually sits
 * ON the curve (not the straight chord midpoint between control
 * points, which is generally floating in empty space for any non-
 * trivial bend).
 *
 * Returns `{ d, segments }` where each segment carries the four
 * Bezier control points (P0, P1, P2, P3). The renderer uses `d`
 * to draw the line and feeds `segments` into `bezierMidpoint(s)`
 * to place the hollow "add waypoint" handles right on the curve.
 */
type BezierSegment = { p0: Point; p1: Point; p2: Point; p3: Point };

function buildBezierThroughPoints(
  points: Point[],
  sourcePos: Position,
  targetPos: Position,
): { d: string; segments: BezierSegment[] } {
  if (points.length < 2) return { d: '', segments: [] };

  // Endpoint tangents are STRICTLY perpendicular to the handle
  // face — no chord-direction blending. This keeps the curve
  // leaving / entering each node at exactly 90° from the side
  // its connector sits on, matching React Flow's stock bezier
  // behaviour and the user's expectation that a "right" handle
  // emits a horizontal exit no matter where the target sits.
  // Interior waypoint tangents stay Catmull-Rom (chord through
  // the previous and next control points) so they stay smooth.
  const tangents: Point[] = points.map((_, i) => {
    if (i === 0) return tangentOf(sourcePos);
    if (i === points.length - 1) {
      const t = tangentOf(targetPos);
      return { x: -t.x, y: -t.y };
    }
    return normalise({
      x: points[i + 1].x - points[i - 1].x,
      y: points[i + 1].y - points[i - 1].y,
    });
  });

  const segments: BezierSegment[] = [];
  let d = `M ${points[0].x} ${points[0].y}`;
  const lastIdx = points.length - 1;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p3 = points[i + 1];
    const segLen = Math.hypot(p3.x - p0.x, p3.y - p0.y);
    // 0.33 is the Catmull-Rom-equivalent tension. Tighter (lower)
    // gives more polyline-ish segments; looser (higher) starts
    // overshooting around waypoints with sharp angles.
    const baseArm = segLen * 0.33;
    // At the source / target ends, bump the arm to at least
    // KEEP_OUT (clamped to half the segment so we don't overshoot
    // past `p3` / `p0`). This forces the cubic to hold the
    // perpendicular handle direction for ≥24px before bending,
    // matching the smoothstep lead-in / lead-out keep-out.
    const halfSeg = segLen / 2;
    const armSrc = i === 0 ? Math.min(halfSeg, Math.max(KEEP_OUT, baseArm)) : baseArm;
    const armDst =
      i === lastIdx - 1 ? Math.min(halfSeg, Math.max(KEEP_OUT, baseArm)) : baseArm;
    const p1 = {
      x: p0.x + tangents[i].x * armSrc,
      y: p0.y + tangents[i].y * armSrc,
    };
    const p2 = {
      x: p3.x - tangents[i + 1].x * armDst,
      y: p3.y - tangents[i + 1].y * armDst,
    };
    segments.push({ p0, p1, p2, p3 });
    d += ` C ${p1.x} ${p1.y}, ${p2.x} ${p2.y}, ${p3.x} ${p3.y}`;
  }
  return { d, segments };
}

/** Point on a cubic Bezier at parameter `t` ∈ [0, 1]. */
function bezierPoint(seg: BezierSegment, t: number): Point {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * seg.p0.x + b * seg.p1.x + c * seg.p2.x + d * seg.p3.x,
    y: a * seg.p0.y + b * seg.p1.y + c * seg.p2.y + d * seg.p3.y,
  };
}

function buildStraightPath(points: Point[]): string {
  if (points.length < 2) return '';
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    d += ` L ${points[i].x} ${points[i].y}`;
  }
  return d;
}

const STEP_CORNER_R = 12;

/**
 * Given a polyline, build an SVG path that rounds EVERY corner
 * with a quarter-arc tangent to both legs. Same geometry Figjam
 * uses for its "smooth step" connector — every direction change
 * (including the joins at user-placed waypoints) reads as one
 * intentional bend rather than an aliased pixel kink.
 *
 * Skips collinear vertices and clamps the corner radius to half
 * the shorter incident leg so the arc never overruns its own
 * legs on short segments.
 */
function roundedPolyline(points: Point[], radius: number): string {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const inDx = curr.x - prev.x;
    const inDy = curr.y - prev.y;
    const inLen = Math.hypot(inDx, inDy);
    const outDx = next.x - curr.x;
    const outDy = next.y - curr.y;
    const outLen = Math.hypot(outDx, outDy);
    if (inLen < 0.1 || outLen < 0.1) {
      d += ` L ${curr.x} ${curr.y}`;
      continue;
    }
    const inUx = inDx / inLen;
    const inUy = inDy / inLen;
    const outUx = outDx / outLen;
    const outUy = outDy / outLen;
    // Cosine of the bend angle between the two legs. ≥ ~0.99
    // means the path goes straight through — no arc, no `L`
    // doubling that would otherwise emit a duplicate point.
    const dot = inUx * outUx + inUy * outUy;
    if (dot > 0.999) {
      d += ` L ${curr.x} ${curr.y}`;
      continue;
    }
    const r = Math.min(radius, inLen / 2, outLen / 2);
    const arcStartX = curr.x - inUx * r;
    const arcStartY = curr.y - inUy * r;
    const arcEndX = curr.x + outUx * r;
    const arcEndY = curr.y + outUy * r;
    d += ` L ${arcStartX} ${arcStartY}`;
    d += ` Q ${curr.x} ${curr.y} ${arcEndX} ${arcEndY}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
}

/**
 * Compute the per-segment L-bend corner for a smoothstep path.
 * Each USER segment (between adjacent filled handles) becomes one
 * L-bend; the corner sits on the dominant axis at the point where
 * the path changes direction. We return one corner per segment so
 * callers can both render the path and place "add waypoint"
 * hollow handles right on the visible bend.
 *
 * Returns `null` when the segment is so short on one axis that
 * we collapse it to a straight line (and therefore has no corner).
 */
function smoothStepCorner(p1: Point, p2: Point): Point | null {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  if (Math.abs(dx) < 0.5 || Math.abs(dy) < 0.5) return null;
  const horizFirst = Math.abs(dx) >= Math.abs(dy);
  return horizFirst ? { x: p2.x, y: p1.y } : { x: p1.x, y: p2.y };
}

/**
 * Smoothstep (right-angled) routing through an arbitrary number
 * of waypoints. We expand each user segment into an L-bend, then
 * feed the resulting polyline (user points + L-bend corners)
 * into `roundedPolyline` so EVERY direction change — including
 * the joins at user-placed waypoints — gets the same 12px
 * quarter-arc rounding. Previously the joins emitted a sharp
 * 90° kink that broke the "all rounded" promise of the smoothstep
 * routing mode.
 *
 * `leadIn` / `leadOut` are optional KEEP_OUT extensions of the
 * source / target perpendicular. When present, the polyline is
 * extended with these points so the path always leaves and
 * arrives perpendicular for ≥KEEP_OUT before bending.
 */
function buildSmoothStepThroughPoints(
  points: Point[],
  leadIn?: Point | null,
  leadOut?: Point | null,
): string {
  if (points.length < 2) return '';
  // Augment the user-visible polyline with the keep-out
  // extensions. The smoothstep L-bend logic then routes through
  // them — between (leadIn, first interior waypoint), between
  // interior waypoints, and between (last interior waypoint,
  // leadOut). Source → leadIn and leadOut → target are collinear
  // with the handle perpendicular and stay straight.
  const augmented: Point[] = [points[0]];
  if (leadIn) augmented.push(leadIn);
  for (let i = 1; i < points.length - 1; i++) augmented.push(points[i]);
  if (leadOut) augmented.push(leadOut);
  augmented.push(points[points.length - 1]);

  const polyline: Point[] = [augmented[0]];
  for (let i = 0; i < augmented.length - 1; i++) {
    const corner = smoothStepCorner(augmented[i], augmented[i + 1]);
    if (corner) polyline.push(corner);
    polyline.push(augmented[i + 1]);
  }
  return roundedPolyline(polyline, STEP_CORNER_R);
}

/**
 * Custom React Flow edge that renders a workspace `connector` item.
 *
 * The connector's source / target ids, styling (arrow head, stroke,
 * label) AND waypoints all live on the workspace_item's
 * `state.cache`, NOT on the RF edge itself. The canvas's edge memo
 * projects each `connector` item into one RF Edge with
 * `{ type: 'connector', data: { itemId } }`, and this component
 * reads the rest from the store via `useWorkspaceItem`.
 *
 * Why store-driven rather than via RF edge data: keeps the source
 * of truth in one place (workspace_items) so right-click stroke /
 * arrow / waypoint changes flow through the same `patchItemState`
 * path as every other widget setting.
 */
function ConnectorEdgeImpl({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  data,
}: EdgeProps) {
  const itemId =
    (data as { itemId?: string } | undefined)?.itemId ?? (id as string);
  const item = useWorkspaceItem(itemId);
  const cache = (item?.state.cache ?? {}) as {
    label?: string;
    arrow?: ConnectorArrow;
    strokeStyle?: AnnotationStroke;
    edgeShape?: EdgeShape;
    waypoints?: Waypoint[];
  };
  const label = cache.label ?? '';
  const arrow = cache.arrow ?? 'arrow';
  const stroke = cache.strokeStyle ?? 'solid';
  // Default to smoothstep so new connectors get strict 90° Manhattan
  // routing (horizontal + vertical legs joined by a small rounded
  // corner) instead of a smooth bezier sweep. The user feedback was
  // unambiguous: "must be 90, not 0, not 180, not anything in-between".
  // A cubic bezier exits the source perpendicular to the handle and
  // enters the target perpendicular to its handle, but in the middle
  // the angle varies continuously — that "in-between" sweep is what
  // looks wrong against a Confluence / FigJam mental model. Step
  // routing keeps every visible segment at a strict 0° or 90° to
  // the canvas axes.
  //
  // Users who explicitly want a smooth curve can still pick "Curved"
  // from the edge toolbar; we just don't default to it any more.
  const edgeShape: EdgeShape = cache.edgeShape ?? 'smoothstep';
  const persistedWaypoints = cache.waypoints ?? [];

  // Local override of waypoints during a drag. We update this on
  // every pointer move so the path / handles track the cursor at
  // 60Hz without spamming the store. The committed copy in the
  // cache is only written on pointer-up via `commitWaypoints`,
  // which keeps the persistence layer (and any subscribers) calm.
  // Drafts are kept in the relative `{t, n}` representation —
  // identical to what we persist — so a re-render mid-drag picks
  // up the freshly-derived absolute positions automatically when
  // the source / target endpoint moves.
  const [draftWaypoints, setDraftWaypoints] = useState<WaypointRel[] | null>(
    null,
  );
  const waypoints: Waypoint[] = draftWaypoints ?? persistedWaypoints;

  // Endpoints are pushed outward by HANDLE_RADIUS so the arrowhead
  // tip sits just outside the handle dot. See note on
  // `HANDLE_RADIUS` above.
  const [adjSourceX, adjSourceY] = useMemo(
    () => shiftOutward(sourceX, sourceY, sourcePosition, HANDLE_RADIUS),
    [sourceX, sourceY, sourcePosition],
  );
  const [adjTargetX, adjTargetY] = useMemo(
    () => shiftOutward(targetX, targetY, targetPosition, HANDLE_RADIUS),
    [targetX, targetY, targetPosition],
  );

  // Reference frame for the relative waypoint math: the actual
  // widget-handle positions (un-shifted), so the bend stays
  // pinned to the handle dot when the user drags either widget.
  // The path itself uses outward-shifted endpoints (further down)
  // so the arrowhead clears the handle dot, but the waypoint
  // frame and the endpoint markers stay on the dot for visual
  // continuity.
  const frameSource: Point = useMemo(
    () => ({ x: sourceX, y: sourceY }),
    [sourceX, sourceY],
  );
  const frameTarget: Point = useMemo(
    () => ({ x: targetX, y: targetY }),
    [targetX, targetY],
  );
  const waypointsAbs: Point[] = useMemo(
    () => waypoints.map((w) => toAbsolute(w, frameSource, frameTarget)),
    [waypoints, frameSource, frameTarget],
  );

  // Path build: stitch source → waypoints → target into a single
  // SVG `d` string. The path uses outward-shifted endpoints so
  // the arrowhead lands just outside the handle dot. All three
  // routing modes share the same point list; only the joining
  // geometry differs.
  const pathSource: Point = useMemo(
    () => ({ x: adjSourceX, y: adjSourceY }),
    [adjSourceX, adjSourceY],
  );
  const pathTarget: Point = useMemo(
    () => ({ x: adjTargetX, y: adjTargetY }),
    [adjTargetX, adjTargetY],
  );
  const allPoints: Point[] = useMemo(
    () => [pathSource, ...waypointsAbs, pathTarget],
    [pathSource, waypointsAbs, pathTarget],
  );

  // Perpendicular keep-out extensions off the source / target
  // handles. These are NOT exposed as user-visible waypoints — they
  // only shape the rendered path so it leaves / arrives
  // perpendicular for ≥KEEP_OUT before bending. Skipped when the
  // adjacent path point isn't in the handle's outward direction
  // (would otherwise double-back through the widget).
  const leadIn: Point | null = useMemo(() => {
    if (allPoints.length < 2) return null;
    const next = allPoints[1];
    if (!shouldLead(next, pathSource, sourcePosition)) return null;
    const [x, y] = shiftOutward(
      pathSource.x,
      pathSource.y,
      sourcePosition,
      KEEP_OUT,
    );
    return { x, y };
  }, [allPoints, pathSource, sourcePosition]);
  const leadOut: Point | null = useMemo(() => {
    if (allPoints.length < 2) return null;
    const prev = allPoints[allPoints.length - 2];
    if (!shouldLead(prev, pathTarget, targetPosition)) return null;
    const [x, y] = shiftOutward(
      pathTarget.x,
      pathTarget.y,
      targetPosition,
      KEEP_OUT,
    );
    return { x, y };
  }, [allPoints, pathTarget, targetPosition]);

  // Points used by the bezier builder include the keep-out
  // extensions so the cubic's endpoint arms have something to lean
  // on. The bezier endpoint-arm bump inside `buildBezierThroughPoints`
  // takes over from here — the leadIn / leadOut points themselves
  // shouldn't be inserted (they'd create an awkward second control
  // point), so we leave the bezier list unchanged and let the arm
  // bump enforce the 24px perpendicular run.

  const { path, bezierSegments } = useMemo(() => {
    switch (edgeShape) {
      case 'smoothstep':
        return {
          path: buildSmoothStepThroughPoints(allPoints, leadIn, leadOut),
          bezierSegments: [] as BezierSegment[],
        };
      case 'straight':
        return {
          path: buildStraightPath(allPoints),
          bezierSegments: [] as BezierSegment[],
        };
      case 'bezier':
      default: {
        const { d, segments } = buildBezierThroughPoints(
          allPoints,
          sourcePosition,
          targetPosition,
        );
        return { path: d, bezierSegments: segments };
      }
    }
  }, [edgeShape, allPoints, leadIn, leadOut, sourcePosition, targetPosition]);

  // Per-segment "add waypoint" handle positions, anchored ON the
  // visible curve so the hollow dots track the line a user is
  // looking at instead of floating in the chord's empty middle.
  //   bezier      → sample t=0.5 of each cubic segment.
  //   straight    → chord midpoint (already on the line).
  //   smoothstep  → the L-bend corner of each segment, which
  //                 also sits on the visible path AND is the
  //                 spot a user naturally aims at when they
  //                 want to bend the route somewhere else. Falls
  //                 back to chord midpoint when the segment is
  //                 too short on one axis to L-bend.
  const segmentMidpoints: Point[] = useMemo(() => {
    if (allPoints.length < 2) return [];
    if (edgeShape === 'bezier' && bezierSegments.length > 0) {
      return bezierSegments.map((seg) => bezierPoint(seg, 0.5));
    }
    const out: Point[] = [];
    const last = allPoints.length - 1;
    for (let i = 0; i < last; i++) {
      // For smoothstep, the visible L-bend of each user segment
      // sits between the keep-out lead points (when present),
      // not the raw handle positions. Substituting leadIn /
      // leadOut here keeps the hollow "add waypoint" dot on the
      // visible corner of the rendered path.
      const a = i === 0 && leadIn ? leadIn : allPoints[i];
      const b = i === last - 1 && leadOut ? leadOut : allPoints[i + 1];
      if (edgeShape === 'smoothstep') {
        const corner = smoothStepCorner(a, b);
        if (corner) {
          out.push(corner);
          continue;
        }
      }
      out.push({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    }
    return out;
  }, [edgeShape, bezierSegments, allPoints, leadIn, leadOut]);

  // Toolbar anchor sits at the visual midpoint of the routed
  // path. For an odd segment count we use the middle segment's
  // on-curve midpoint; for an even count we average the two
  // middle midpoints. This lands the toolbar on the line itself
  // even when the curve bows far away from the chord.
  const { labelX, labelY } = useMemo(() => {
    if (segmentMidpoints.length === 0) {
      return { labelX: frameSource.x, labelY: frameSource.y };
    }
    if (segmentMidpoints.length % 2 === 1) {
      const m = segmentMidpoints[(segmentMidpoints.length - 1) / 2];
      return { labelX: m.x, labelY: m.y };
    }
    const a = segmentMidpoints[segmentMidpoints.length / 2 - 1];
    const b = segmentMidpoints[segmentMidpoints.length / 2];
    return { labelX: (a.x + b.x) / 2, labelY: (a.y + b.y) / 2 };
  }, [segmentMidpoints, frameSource]);

  const dashArray =
    stroke === 'dashed' ? '8 6' : stroke === 'dotted' ? '2 4' : undefined;
  // `'none'` hides the stroke entirely — kept for legacy connectors
  // that were saved before the toolbar dropped the option. The
  // wider invisible interaction stroke still receives clicks.
  const visibleStroke = stroke === 'none' ? 'transparent' : 'currentColor';

  // Distinct marker ids per edge instance so multiple connectors
  // can each declare their own arrowhead without collisions in the
  // SVG <defs> namespace.
  const markerEnd = arrow === 'none' ? undefined : `url(#ws-arrow-end-${id})`;
  const markerStart =
    arrow === 'arrow-both' ? `url(#ws-arrow-start-${id})` : undefined;

  // ---- waypoint editing -------------------------------------------------
  const flow = useReactFlow();
  const { patchItemState } = useWorkspaceActions();

  const commitWaypoints = useCallback(
    (next: WaypointRel[]) => {
      patchItemState(itemId, { cache: { waypoints: next } });
    },
    [patchItemState, itemId],
  );

  return (
    <>
      {/* Per-edge arrowhead defs. The marker geometry follows React
          Flow's own ArrowClosed convention — a `-10..10` viewBox with
          the tip at the origin so `refX=0`/`refY=0` lines the
          arrowhead's tip up exactly with the path's endpoint. We use
          `markerUnits="strokeWidth"` so the arrow grows with the
          stroke (matches our selected vs default weights) and
          `orient="auto-start-reverse"` so the same marker is reused
          for the start side, automatically flipped to point inward. */}
      <defs>
        <marker
          id={`ws-arrow-end-${id}`}
          viewBox="-10 -10 20 20"
          refX="0"
          refY="0"
          markerWidth="10"
          markerHeight="10"
          orient="auto-start-reverse"
          markerUnits="strokeWidth"
        >
          <path
            d="M -7 -5 L 0 0 L -7 5 Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        </marker>
        <marker
          id={`ws-arrow-start-${id}`}
          viewBox="-10 -10 20 20"
          refX="0"
          refY="0"
          markerWidth="10"
          markerHeight="10"
          orient="auto-start-reverse"
          markerUnits="strokeWidth"
        >
          <path
            d="M -7 -5 L 0 0 L -7 5 Z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        </marker>
      </defs>

      {/* Invisible hit target — 16px wide so right-click + drag work
          even when the visible stroke is hair-thin. Kept stroke-only
          and non-rendered so the pointer surface stays under the
          curve but adds zero visual weight. */}
      <path
        d={path}
        fill="none"
        strokeWidth={16}
        stroke="transparent"
        className="react-flow__edge-interaction"
      />

      <path
        id={id}
        d={path}
        fill="none"
        // CRITICAL: SVG's default `stroke` is `none`, not
        // `currentColor`. Without this attribute the path is
        // completely invisible no matter how big `strokeWidth` is —
        // same trap that bit Mermaid and ELK for years.
        // `currentColor` lets us drive the actual color via the
        // className's `text-*` utility, which gives us theme-aware
        // tone without re-declaring it per branch. When
        // `stroke === 'none'` we override this to `transparent` so
        // the line disappears while the wider invisible hit target
        // (path above) still receives clicks.
        stroke={visibleStroke}
        // Selected state bumps stroke weight + flips to primary
        // tone so the user can SEE the line they just clicked
        // before reaching for Delete / the edge context menu.
        strokeWidth={selected ? 2.5 : 1.5}
        strokeDasharray={dashArray}
        markerEnd={markerEnd}
        markerStart={markerStart}
        className={
          selected
            ? 'ws-connector-stroke ws-connector-stroke-selected text-foreground'
            : 'ws-connector-stroke text-foreground'
        }
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute pointer-events-auto"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            }}
          >
            <span className="glass-surface inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-medium text-foreground shadow-sm">
              {label}
            </span>
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Waypoint editing UI — only when the edge is selected.
          Endpoint markers use the UN-shifted source / target so
          they sit exactly on top of the widget connector dot
          (the path itself still uses the outward-shifted ends so
          the arrowhead doesn't get obscured by that dot). Result:
          one combined visual at each end instead of a floating
          marker hovering past the widget's connector. */}
      {selected && (
        <WaypointLayer
          edgeId={id}
          waypointsAbs={waypointsAbs}
          segmentMidpoints={segmentMidpoints}
          source={frameSource}
          target={frameTarget}
          flow={flow}
          onDraft={(nextAbs) => {
            if (nextAbs === null) {
              setDraftWaypoints(null);
              return;
            }
            setDraftWaypoints(
              nextAbs.map((p) => absToRel(p, frameSource, frameTarget)),
            );
          }}
          onCommit={(nextAbs) => {
            const rel = nextAbs.map((p) =>
              absToRel(p, frameSource, frameTarget),
            );
            setDraftWaypoints(null);
            commitWaypoints(rel);
          }}
        />
      )}

      {/* Floating Figjam-style toolbar — visible only when the edge
          is selected. Positioned at the path's midpoint via
          `EdgeLabelRenderer`. Survives pan / zoom and lives in a
          portal above the canvas. */}
      {selected && (
        <ConnectorEdgeToolbar
          edgeId={id}
          itemId={itemId}
          centerX={labelX}
          centerY={labelY}
          selected={selected}
        />
      )}
    </>
  );
}

export const ConnectorEdge = memo(ConnectorEdgeImpl);

// ---- waypoint UI -----------------------------------------------------------

// Inline styles for endpoint / waypoint dots. Mirrors
// `.ws-conn-handle` (the widget connector dot in globals.css) so
// the two affordances share the same visual vocabulary: solid
// accent for "this anchors something" (endpoints, filled
// waypoints), hollow accent ring for "drag here to add a new
// waypoint" (midpoints) — same grammar as the widget connector
// dots, which is also a hollow accent ring with white fill.
//
// Inline rather than a custom globals.css selector because the
// edgelabel-renderer portal occasionally lands inside a stacking
// context that trips a CSS-utility ordering bug, leaving the
// dots invisible. Inline styles paint unconditionally, sidestepping
// that whole class of problem. Theme awareness comes from the
// `--ws-edge-accent` CSS variable (defined in globals.css with a
// light + dark value) so the dots still recolour automatically.
const EDGE_FILLED_STYLE = {
  background: 'var(--ws-edge-accent)',
  boxShadow:
    '0 0 0 2px var(--color-background, #ffffff), 0 1px 2px rgba(15, 23, 42, 0.25)',
} as const;

const EDGE_HOLLOW_STYLE = {
  background: 'var(--color-background, #ffffff)',
  boxShadow:
    '0 0 0 2px var(--ws-edge-accent), 0 1px 2px rgba(15, 23, 42, 0.18)',
} as const;

type WaypointLayerProps = {
  edgeId: string;
  // Absolute (flow-coord) positions of the current waypoints. The
  // store keeps these in the source→target reference frame; the
  // parent component does the conversion before handing them to
  // us so the layer doesn't have to know about the relative
  // representation.
  waypointsAbs: Point[];
  // One per path SEGMENT (waypoints + endpoints − 1). Each entry
  // is the on-curve midpoint of that segment, used to position
  // the hollow "drag to add a waypoint" handles right on the
  // visible line (not the chord, which floats in empty space for
  // any non-trivial bend).
  segmentMidpoints: Point[];
  source: Point;
  target: Point;
  flow: ReturnType<typeof useReactFlow>;
  onDraft: (nextAbs: Point[] | null) => void;
  onCommit: (nextAbs: Point[]) => void;
};

/**
 * Waypoint editing layer. Confluence / FigJam style:
 *
 *   ●  filled — an existing waypoint. Drag to move, double-click
 *      to remove.
 *   ○  hollow — a midpoint between two adjacent control points.
 *      Dragging it splices a NEW filled waypoint into the path at
 *      that index, then continues the drag on that new waypoint.
 *
 * The source / target endpoints render as filled handles too, but
 * they're decorative here — reconnection (drag endpoint onto a
 * different node) is wired through React Flow's built-in
 * `reconnectable` + `onReconnect` plumbing in workspace-canvas. So
 * we render the endpoint dots with `pointer-events: none` to keep
 * them from competing with RF's reconnect zones underneath.
 */
function WaypointLayer({
  waypointsAbs,
  segmentMidpoints,
  source,
  target,
  flow,
  onDraft,
  onCommit,
}: WaypointLayerProps) {
  // Drag state mirrors the local draft. We hold the current
  // waypoint array (absolute coords) in a ref so pointermove can
  // mutate without closing over a stale array.
  const dragRef = useRef<{
    index: number;
    waypoints: Point[];
    pointerId: number;
  } | null>(null);

  const startDrag = useCallback(
    (e: React.PointerEvent, index: number, initial: Point[]) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
      } catch {
        // Older Safari may throw — selection still works.
      }
      dragRef.current = { index, waypoints: initial, pointerId: e.pointerId };
      onDraft(initial);
    },
    [onDraft],
  );

  const onMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const flowPos = flow.screenToFlowPosition({
        x: e.clientX,
        y: e.clientY,
      });
      const next = drag.waypoints.slice();
      next[drag.index] = { x: flowPos.x, y: flowPos.y };
      drag.waypoints = next;
      onDraft(next);
    },
    [flow, onDraft],
  );

  const endDrag = useCallback(
    (e: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      try {
        (e.currentTarget as Element).releasePointerCapture(drag.pointerId);
      } catch {
        // Capture might have been lost already; safe to ignore.
      }
      const final = drag.waypoints;
      dragRef.current = null;
      onCommit(final);
    },
    [onCommit],
  );

  const removeWaypoint = useCallback(
    (index: number) => {
      const next = waypointsAbs.slice();
      next.splice(index, 1);
      onCommit(next);
    },
    [waypointsAbs, onCommit],
  );

  // Filled handle = source ▢ waypoints ▢ target. Hollow handles
  // sit at the ON-CURVE midpoint of each adjacent pair of filled
  // handles (the parent computes these from the cubic-bezier
  // segments so they track the visible line). Dragging a hollow
  // inserts a new waypoint and continues the drag on it; dragging
  // a filled waypoint moves it; double-clicking removes it.
  const filled = useMemo(
    () => [source, ...waypointsAbs, target],
    [source, waypointsAbs, target],
  );

  return (
    <EdgeLabelRenderer>
      {/* Filled handles. Index 0 = source, last = target — both
          rendered as decorative dots (pointer-events: none) so
          they don't fight React Flow's reconnect zones. */}
      {filled.map((pt, i) => {
        const isEndpoint = i === 0 || i === filled.length - 1;
        const waypointIndex = i - 1;
        if (isEndpoint) {
          return (
            <div
              key={`f-${i}`}
              className="nodrag nopan absolute ws-edge-endpoint"
              style={{
                pointerEvents: 'none',
                transform: `translate(-50%, -50%) translate(${pt.x}px, ${pt.y}px)`,
              }}
            >
              <span
                className="block h-3 w-3 rounded-full"
                style={EDGE_FILLED_STYLE}
              />
            </div>
          );
        }
        return (
          <div
            key={`f-${i}`}
            className={cn(
              'nodrag nopan absolute pointer-events-auto',
              'ws-edge-handle ws-edge-filled',
              'cursor-grab active:cursor-grabbing',
            )}
            style={{
              transform: `translate(-50%, -50%) translate(${pt.x}px, ${pt.y}px)`,
            }}
            onPointerDown={(e) => startDrag(e, waypointIndex, waypointsAbs)}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onDoubleClick={(e) => {
              e.stopPropagation();
              removeWaypoint(waypointIndex);
            }}
          >
            <span
              className="block h-3 w-3 rounded-full transition-transform hover:scale-110"
              style={EDGE_FILLED_STYLE}
            />
          </div>
        );
      })}
      {/* Hollow "add waypoint" handles — one per path segment,
          anchored on the ON-CURVE midpoint of that segment so
          they track the visible line rather than the chord. The
          insertion index in the waypoints array is the segment
          index: segment `i` lives between `filled[i]` and
          `filled[i+1]`, so the new waypoint goes BEFORE
          `filled[i+1]` in the waypoints array → index `i`. */}
      {segmentMidpoints.map((mid, i) => (
        <div
          key={`m-${i}`}
          className={cn(
            'nodrag nopan absolute pointer-events-auto',
            'ws-edge-handle ws-edge-hollow',
            'cursor-grab active:cursor-grabbing',
          )}
          style={{
            transform: `translate(-50%, -50%) translate(${mid.x}px, ${mid.y}px)`,
          }}
          onPointerDown={(e) => {
            // Splice the new waypoint at the on-curve midpoint
            // and immediately start dragging from that index.
            // Initial draft already contains the inserted point
            // so the bend renders through it from frame 0.
            const seeded = waypointsAbs.slice();
            seeded.splice(i, 0, { x: mid.x, y: mid.y });
            startDrag(e, i, seeded);
          }}
          onPointerMove={onMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <span
            className="block h-2.5 w-2.5 rounded-full transition-transform hover:scale-125"
            style={EDGE_HOLLOW_STYLE}
          />
        </div>
      ))}
    </EdgeLabelRenderer>
  );
}
